<template>
  <v-container fluid class="pa-0 ma-0 full-height">
    <v-card v-if="locationDataMissing" flat class="full-height">
      <v-card-text class="full-height">
        <v-row class="full-height" align="center" justify="center">
          <v-col cols="11" sm="9">
            <h1 class="text-h6 d-flex mb-5">
              <v-icon color="primary" class="mr-2"
                >mdi-information-outline</v-icon
              >
              {{ $t("$vuetify.noDataText") }}
            </h1>
            <p class="pl-8">
              {{ $t("machines.system.noDataDescription") }}
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card v-if="!locationDataMissing" flat border class="fill-height">
      <v-progress-linear
        :active="isLoading"
        :indeterminate="isLoading"
        absolute
        top
      ></v-progress-linear>
      <div id="locationMap"></div>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex"
import { initialize } from "@/utils/googlemapsadapter"
import EditableCircle from "@/utils/googlemaps/editablecircle"
import localeDateFilter from "@/filters/localedatefilter"

export default {
  // showHistory/historyDates are owned by Location.vue, which renders the
  // "show history" checkbox and date range separately, below both the map
  // and the geofence card, so those two cards can match heights.
  props: ["machine", "showHistory", "historyDates"],
  data() {
    return {
      //Map objects
      map: null,
      polilyne: null,
      marker: null,
      circle: null,
      drawingControlDiv: null,
      isDrawingMode: false,
      mapClickListener: null,

      isMapInitialized: false,
      defaultCenterPosition: { lat: 64.7448271, lng: 21.0342396 },
      currentLocation: null,

      locationDataMissing: false
    }
  },
  async created() {
    await this.$store.dispatch("fetchCurrentMachineLocalization", this.machine)
    if (!this.currentMachineLocalization) {
      this.locationDataMissing = true
      return
    }
    this.fetchMachineLocalizationHistory()
    this.configureMap()
  },
  computed: {
    ...mapGetters([
      "canReadGeofence",
      "canEditGeofence",
      "currentMachineLocalization"
    ]),

    locationHistoryCoordinates() {
      return this.showHistory
        ? this.$store.getters.machineLocalizationHistory
        : null
    },
    isLoading() {
      return (
        this.$store.getters.loadingMachineLocalizationHistory ||
        this.$store.getters.loadingMachineGeofencingSettings
      )
    },
    latitude() {
      return this.$store.getters.geofenceAreaLatitude
    },
    longitude() {
      return this.$store.getters.geofenceAreaLongitude
    },
    radius() {
      return this.$store.getters.geofenceAreaRadius
    },
    unit() {
      return this.$store.getters.geofenceAreaUnit
    },
    geofenceAreaIsUpdating() {
      return this.$store.getters.geofenceAreaIsUpdating
    },
    editModeEnabled: {
      get() {
        return this.$store.getters.geofenceAreaEditMode
      },
      set(val) {
        this.$store.dispatch("setGeofenceAreaEditMode", val)
      }
    },
    geofenceAreaSettings() {
      return {
        lat: this.latitude,
        lng: this.longitude,
        radius: this.radius,
        editModeEnabled: this.editModeEnabled
      }
    },
    supported() {
      return this.$store.getters.machineGeofencingSettings?.capabilities
        ?.notifications
    }
  },
  watch: {
    geofenceAreaIsUpdating: function() {
      if (!this.geofenceAreaIsUpdating) {
        this.setGeofenceArea(
          this.geofenceAreaSettings.lat,
          this.geofenceAreaSettings.lng,
          this.geofenceAreaSettings.radius,
          this.geofenceAreaSettings.editModeEnabled
        )
      }
    },
    geofenceAreaSettings: function() {
      this.setGeofenceArea(
        this.geofenceAreaSettings.lat,
        this.geofenceAreaSettings.lng,
        this.geofenceAreaSettings.radius,
        this.geofenceAreaSettings.editModeEnabled
      )
      this.updateDrawingManager()
    },
    machine: function() {
      this.configureMap()
    },
    locationHistoryCoordinates: function() {
      this.updateHistory()
    },
    historyDates: function() {
      if (!this.showHistory) {
        return
      }
      this.fetchMachineLocalizationHistory()
    },
    showHistory: function() {
      if (this.showHistory) {
        this.fetchMachineLocalizationHistory()
      }
      this.updateHistory()
    },
    isLoading: function() {
      const setGeofenceHandler = () => {
        this.setGeofenceArea(
          this.geofenceAreaSettings.lat,
          this.geofenceAreaSettings.lng,
          this.geofenceAreaSettings.radius,
          this.geofenceAreaSettings.editModeEnabled
        )
      }
      if (!this.isLoading && !this.isMapInitialized) {
        this.configureMap().then(() => setGeofenceHandler())
      } else {
        setGeofenceHandler()
      }
    },
    supported() {
      this.updateDrawingManager()
    },
    canEditGeofence: function() {
      this.updateDrawingManager()
    }
  },
  methods: {
    onCircleEdit(args) {
      if (this.$store.getters.geofenceAreaIsUpdating) {
        return
      }
      this.$store.dispatch("updateGeofenceArea", args)
    },
    fetchMachineLocalizationHistory() {
      if (
        this.historyDates?.length == 2 &&
        this.historyDates[0] &&
        this.historyDates[1]
      ) {
        const params = {
          tenantUid: this.machine.tenantUid,
          serialNo: this.machine.serialNo,
          start: this.historyDates[0],
          end: this.historyDates[1]
        }
        this.$store.dispatch("fetchMachineLocalizationHistory", params)
      }
    },
    updateHistory() {
      if (!this.map) {
        return
      }
      const google = window.google
      if (this.polilyne) {
        this.polilyne.setMap(null)
        this.polilyne = null
      }
      if (this.showHistory && this.locationHistoryCoordinates) {
        this.polilyne = new google.maps.Polyline({
          path: this.locationHistoryCoordinates,
          geodesic: true,
          strokeColor: "#000000",
          strokeOpacity: 0.5,
          strokeWeight: 2
        })
        this.polilyne.setMap(this.map)
      }
    },
    setGeofenceArea(lat, lng, radius, editMode) {
      if (
        this.$store.getters.loadingMachineGeofencingSettings ||
        this.$store.getters.geofenceAreaIsUpdating ||
        !this.canReadGeofence
      ) {
        return
      }
      if (!lat || !lng || !radius) {
        this.removeGeofenceArea()
        return
      }
      if (
        this.circle &&
        this.circle.lat == lat &&
        this.circle.lng == lng &&
        this.circle.radius == radius
      ) {
        this.circle.setEditable(editMode)
        return
      }
      if (!this.circle) {
        this.circle = new EditableCircle()
      }
      this.circle.setCenter({ lat, lng })
      this.circle.setRadius(radius)
      this.circle.setMap(this.map, this.onCircleEdit)
      this.circle.setEditable(editMode)
    },
    removeGeofenceArea() {
      if (this.circle) {
        this.circle.setEditable(false)
        this.circle.setMap(null)
      }
      const position = this?.marker?.position
      if (position) {
        this?.map.setCenter(position)
      }
    },
    saveGeofenceArea() {
      if (this.editModeEnabled) {
        this.$store.dispatch("setGeofenceAreaSave", true) //It is saved on the GeofencingSettings.vue
      }
    },
    updateDrawingManager() {
      if (!this.isMapInitialized) {
        return
      }
      if (this.supported && this.canEditGeofence) {
        this.addDrawingControl()
      } else {
        this.removeDrawingControl()
      }
    },
    // The Drawing library (google.maps.drawing.DrawingManager) was removed
    // from the Maps JavaScript API. This replaces the old "click the toolbar
    // button, then click-drag to draw a circle" flow with a custom map
    // control that arms a single map click, which places the circle at its
    // default size. From there, editing (drag center/resize) still uses the
    // Circle's own built-in editable handles, unaffected by that removal.
    addDrawingControl() {
      if (this.drawingControlDiv) {
        return
      }
      const google = window.google
      const controlDiv = document.createElement("div")
      Object.assign(controlDiv.style, {
        backgroundColor: "#fff",
        border: "2px solid #fff",
        borderRadius: "3px",
        boxShadow: "0 2px 6px rgba(0,0,0,.3)",
        cursor: "pointer",
        margin: "10px",
        padding: "8px 12px",
        fontFamily: "Roboto, sans-serif",
        fontSize: "14px"
      })
      controlDiv.textContent = "Draw a circle"
      controlDiv.addEventListener("click", () => this.onDrawControlClick())
      this.drawingControlDiv = controlDiv
      this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
        controlDiv
      )
    },
    removeDrawingControl() {
      this.cancelDrawingMode()
      if (this.drawingControlDiv) {
        const google = window.google
        const controls = this.map.controls[google.maps.ControlPosition.TOP_CENTER]
        for (let ix = controls.getLength() - 1; ix >= 0; ix--) {
          if (controls.getAt(ix) === this.drawingControlDiv) {
            controls.removeAt(ix)
          }
        }
        this.drawingControlDiv = null
      }
    },
    onDrawControlClick() {
      // If a circle already exists, re-enable editing it instead of drawing
      // a new one, matching the previous drawing-manager behaviour.
      if (this.circle && this.circle.map) {
        this.circle.setEditable(true)
        this.editModeEnabled = true
        return
      }
      if (this.isDrawingMode) {
        this.cancelDrawingMode()
      } else {
        this.startDrawingMode()
      }
    },
    startDrawingMode() {
      const google = window.google
      this.isDrawingMode = true
      this.map.setOptions({ draggableCursor: "crosshair" })
      this.mapClickListener = google.maps.event.addListener(
        this.map,
        "click",
        event => this.onMapClickForDrawing(event)
      )
    },
    cancelDrawingMode() {
      if (!this.isDrawingMode) {
        return
      }
      const google = window.google
      this.isDrawingMode = false
      this.map.setOptions({ draggableCursor: null })
      if (this.mapClickListener) {
        google.maps.event.removeListener(this.mapClickListener)
        this.mapClickListener = null
      }
    },
    onMapClickForDrawing(event) {
      this.cancelDrawingMode()
      this.editModeEnabled = true
      this.onCircleEdit({
        latitude: event.latLng.lat(),
        longitude: event.latLng.lng(),
        radius: this.radius || 300,
        unit: this.unit || "km"
      })
    },
    async configureMap() {
      if (!this.machine || this.isLoading || this.locationDataMissing) {
        return
      }
      this.isMapInitialized = false

      try {
        await initialize()
        const google = window.google

        const mapOptions = {
          mapId: "LOCATION_MAP_ID",
          zoom: 12,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
            position: google.maps.ControlPosition.RIGHT_TOP
          }
        }

        const map = new google.maps.Map(
          document.getElementById("locationMap"),
          mapOptions
        )
        this.map = map

        let position = this.defaultCenterPosition

        if (this.currentMachineLocalization !== null) {
          position = new google.maps.LatLng(
            this.currentMachineLocalization.lat,
            this.currentMachineLocalization.lng
          )
          const recvTime = localeDateFilter(this.currentMachineLocalization.receiveTime)
          const title =
            this.$t("machines.location.map.machineLocation") +
            " - Last updated " +
            recvTime

          const markerImage = document.createElement("img")
          markerImage.src = window.location.origin + "/marker.png"

          this.marker = new google.maps.marker.AdvancedMarkerElement({
            position: position,
            content: markerImage,
            map: map,
            title: title
          })
        }

        this.updateHistory()

        const centerPos = position || this.defaultCenterPosition

        map.setCenter(centerPos)
      } catch (error) {
        console.error(error)
      }
      this.isMapInitialized = true
      // updateDrawingManager() early-returns until isMapInitialized is true,
      // so it must run after the flag is set, not before.
      this.updateDrawingManager()
    }
  }
}
</script>

<style>
.full-height {
  height: 100%;
}

#locationMap {
  height: 60vh;
}
/*The following styles overides default styles for a circle component on the map (2nd for touch devices)*/
#locationMap
  div[style^="position: absolute; left: 0px; top: 0px; width: 9px; height: 9px;"],
#locationMap
  div[style^="position: absolute; left: 9px; top: 9px; width: 9px; height: 9px;"] {
  border-color: white !important;
  border-width: 1px !important;
  background-color: #444444 !important;
}
</style>
